import type {
  RunnerLevel,
  RunnerObstacle,
  RunnerOrbitMotion,
  RunnerPathPingPongMotion,
  RunnerPathPingPongPhase,
} from '../../domain/runner/runnerTypes';

function advancePingPongPhase(
  phase: RunnerPathPingPongPhase,
): RunnerPathPingPongPhase {
  switch (phase) {
    case 'toFinish':
      return 'dwellFinish';
    case 'dwellFinish':
      return 'toStart';
    case 'toStart':
      return 'dwellStart';
    case 'dwellStart':
      return 'toFinish';
  }
}

function pingPongPhaseDuration(motion: RunnerPathPingPongMotion): number {
  if (motion.phase === 'toFinish' || motion.phase === 'toStart') {
    return motion.durationSec;
  }
  return motion.dwellSec;
}

function pingPongCenter(
  motion: RunnerPathPingPongMotion,
  fromStart: boolean,
  t: number,
): { centerS: number; centerLane: number } {
  const clamped = Math.min(1, Math.max(0, t));
  const aS = fromStart ? motion.startS : motion.finishS;
  const aLane = fromStart ? motion.startLane : motion.finishLane;
  const bS = fromStart ? motion.finishS : motion.startS;
  const bLane = fromStart ? motion.finishLane : motion.startLane;
  return {
    centerS: aS + (bS - aS) * clamped,
    centerLane: aLane + (bLane - aLane) * clamped,
  };
}

function tickPathPingPongObstacle(
  obstacle: RunnerObstacle,
  motion: RunnerPathPingPongMotion,
  deltaSeconds: number,
  motionScale: number,
): RunnerObstacle {
  if (motionScale <= 0 || deltaSeconds <= 0) {
    return obstacle;
  }

  let phase = motion.phase;
  let elapsedSec = motion.elapsedSec + deltaSeconds * motionScale;
  let centerS = motion.startS;
  let centerLane = motion.startLane;

  while (true) {
    if (elapsedSec < 0) {
      centerS = motion.startS;
      centerLane = motion.startLane;
      break;
    }

    const duration = pingPongPhaseDuration({ ...motion, phase });
    if (duration <= 0 || elapsedSec >= duration) {
      elapsedSec -= Math.max(duration, 0);
      phase = advancePingPongPhase(phase);
      continue;
    }

    if (phase === 'toFinish') {
      const t = duration <= 0 ? 1 : elapsedSec / duration;
      ({ centerS, centerLane } = pingPongCenter(motion, true, t));
    } else if (phase === 'toStart') {
      const t = duration <= 0 ? 1 : elapsedSec / duration;
      ({ centerS, centerLane } = pingPongCenter(motion, false, t));
    } else if (phase === 'dwellFinish') {
      centerS = motion.finishS;
      centerLane = motion.finishLane;
    } else {
      centerS = motion.startS;
      centerLane = motion.startLane;
    }
    break;
  }

  return {
    ...obstacle,
    hitbox: {
      ...obstacle.hitbox,
      centerS,
      centerLane,
    },
    motion: {
      ...motion,
      phase,
      elapsedSec,
    },
  };
}

function tickOrbitObstacle(
  obstacle: RunnerObstacle,
  motion: RunnerOrbitMotion,
  deltaSeconds: number,
  motionScale: number,
): RunnerObstacle {
  if (motionScale <= 0 || deltaSeconds <= 0) {
    return obstacle;
  }
  const durationSec = motion.durationSec;
  const elapsedSec = motion.elapsedSec + deltaSeconds * motionScale;
  const wrapped = durationSec > 0 ? elapsedSec % durationSec : 0;
  const t = durationSec <= 0 ? 0 : wrapped / durationSec;
  const angle = -t * Math.PI * 2;
  return {
    ...obstacle,
    hitbox: {
      ...obstacle.hitbox,
      centerS: motion.centerS + motion.radiusS * Math.cos(angle),
      centerLane: motion.centerLane + motion.radiusLane * Math.sin(angle),
    },
    motion: {
      ...motion,
      elapsedSec: wrapped,
    },
  };
}

function tickPathObstacle(
  obstacle: RunnerObstacle,
  deltaSeconds: number,
  motionScale: number,
): RunnerObstacle {
  const motion = obstacle.motion;
  if (motion == null || obstacle.consumed) {
    return obstacle;
  }
  if (motion.kind === 'pathPingPong') {
    return tickPathPingPongObstacle(obstacle, motion, deltaSeconds, motionScale);
  }
  if (motion.kind === 'orbit') {
    return tickOrbitObstacle(obstacle, motion, deltaSeconds, motionScale);
  }
  if (motion.kind !== 'path') {
    return obstacle;
  }
  if (motionScale <= 0) {
    return obstacle;
  }
  if (motion.elapsedSec >= motion.durationSec) {
    return obstacle;
  }

  const elapsedSec = Math.min(
    motion.durationSec,
    motion.elapsedSec + deltaSeconds * motionScale,
  );
  const t =
    motion.durationSec <= 0 ? 1 : Math.min(1, elapsedSec / motion.durationSec);
  const visualCenterS = motion.startS + (motion.finishS - motion.startS) * t;
  const visualCenterLane =
    motion.startLane + (motion.finishLane - motion.startLane) * t;
  const arrived = t >= 1;
  const consumeOnArrive = motion.consumeOnArrive !== false;
  const offset = obstacle.hitboxLaneOffset ?? 0;

  return {
    ...obstacle,
    visualCenterS,
    visualCenterLane,
    hitbox: {
      ...obstacle.hitbox,
      centerS: visualCenterS,
      centerLane: visualCenterLane + offset,
    },
    consumed: arrived && consumeOnArrive ? true : obstacle.consumed,
    motion: {
      ...motion,
      elapsedSec,
    },
  };
}

/** Движение path-препятствий; возвращает тот же level, если ничего не изменилось. */
export function tickRunnerPathObstaclesUseCase(
  level: RunnerLevel,
  deltaSeconds: number,
  motionScale = 1,
): RunnerLevel {
  let changed = false;
  const obstacles = level.obstacles.map((obstacle) => {
    const next = tickPathObstacle(obstacle, deltaSeconds, motionScale);
    if (next !== obstacle) {
      changed = true;
    }
    return next;
  });
  if (!changed) {
    return level;
  }
  return { ...level, obstacles };
}
